import { useState, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import { Toaster, toast } from "sonner";
import {
  Globe,
  ShieldCheck,
  MagnifyingGlass,
  CircleNotch,
  HardDrives,
  WarningCircle,
  CheckCircle,
  XCircle,
  Warning,
  Camera,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `/api`;

function DataRow({ label, value, testId, mono = true }) {
  const isEmpty = value === null || value === undefined || value === "";
  return (
    <div className="data-row flex flex-col sm:flex-row sm:items-baseline justify-between px-6 py-3.5" data-testid={testId}>
      <span className="text-xs font-bold text-slate-500 uppercase tracking-[0.15em] sm:w-2/5 shrink-0 font-body">
        {label}
      </span>
      <span className={`${mono ? "font-mono" : "font-body"} text-sm ${isEmpty ? "text-slate-400 italic" : "text-slate-950"} font-medium break-all mt-1 sm:mt-0 sm:text-right`}>
        {isEmpty ? "Not available" : value}
      </span>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    Valid: { className: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle },
    "Expiring Soon": { className: "bg-amber-100 text-amber-800 border-amber-200 badge-pulse", icon: Warning },
    Expired: { className: "bg-rose-100 text-rose-800 border-rose-200", icon: XCircle },
    "Invalid Certificate": { className: "bg-rose-100 text-rose-800 border-rose-200", icon: XCircle },
    "No SSL": { className: "bg-slate-100 text-slate-600 border-slate-200", icon: WarningCircle },
    "Connection Failed": { className: "bg-slate-100 text-slate-600 border-slate-200", icon: WarningCircle },
    Error: { className: "bg-rose-100 text-rose-800 border-rose-200", icon: XCircle },
    Unknown: { className: "bg-slate-100 text-slate-600 border-slate-200", icon: WarningCircle },
    "Invalid domain": { className: "bg-rose-100 text-rose-800 border-rose-200", icon: XCircle },
  };
  const c = config[status] || config.Unknown;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border uppercase tracking-widest ${c.className}`} data-testid="ssl-status-badge">
      <Icon weight="bold" className="w-3.5 h-3.5" />
      {status}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-none card-brutal flex flex-col">
      <div className="px-6 py-5 bg-slate-50 border-b border-slate-200">
        <div className="skeleton-bar w-32 h-5" />
      </div>
      <div className="p-0 flex flex-col divide-y divide-slate-100">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex justify-between px-6 py-4">
            <div className="skeleton-bar w-24 h-3" />
            <div className="skeleton-bar w-40 h-3" />
          </div>
        ))}
      </div>
    </div>
  );
}

function isCyberfolks(registrar) {
  if (!registrar) return false;
  const lower = registrar.toLowerCase().replace(/[_\s.,-]/g, "");
  return lower.includes("cyberfolks");
}

function isInterNetX(registrar) {
  if (!registrar) return false;
  return registrar.toLowerCase().includes("internetx");
}

function ipToArpa(ip) {
  if (!ip) return null;
  const parts = ip.split(".");
  if (parts.length !== 4) return null;
  return `${parts.reverse().join(".")}.in-addr.arpa`;
}

function ActionButtons({ registrar, ipAddress }) {
  const cyberfolks = isCyberfolks(registrar);
  const internetx = isInterNetX(registrar);
  const showGeoTrust = cyberfolks || internetx;

  const sslImage = showGeoTrust
    ? "https://www.meerkatwebhosting.co.uk/templates/HostHub7/html/img/ssl-certificates/geotrust.png"
    : "https://www.meerkatwebhosting.co.uk/templates/HostHub7/html/img/ssl-certificates/certum.png";

  const sslLink = showGeoTrust
    ? "https://login.sslmanager.com/#Vigo.mod.SSLCertificate.doCreate"
    : "https://ssl4less.eu/panel/api/order";

  const sslLabel = showGeoTrust ? "Order GeoTrust SSL" : "Order Certum SSL";

  const whmLink = ipAddress ? `https://${ipAddress}:2087` : "#";

  return (
    <div className="mt-8 flex flex-wrap items-center gap-3 animate-slide-up" data-testid="action-buttons">
      {/* SSL Certificate Button */}
      <a
        href={sslLink}
        target="_blank"
        rel="noopener noreferrer"
        className="action-btn inline-flex items-center bg-white border-2 border-slate-200 rounded-lg px-4 py-2.5 hover:border-[#002FA7] hover:shadow-md transition-all duration-200"
        data-testid="ssl-order-button"
        title={sslLabel}
      >
        <img src={sslImage} alt={sslLabel} className="h-8 w-auto object-contain" data-testid="ssl-order-image" />
      </a>

      {/* WHM Login Button — pushed to the right */}
      <a
        href={whmLink}
        target="_blank"
        rel="noopener noreferrer"
        className={`action-btn ml-auto inline-flex items-center bg-white border-2 border-slate-200 rounded-lg px-4 py-2.5 hover:border-[#002FA7] hover:shadow-md transition-all duration-200 ${!ipAddress ? "opacity-50 pointer-events-none" : ""}`}
        data-testid="whm-login-button"
        title="WHM Login"
      >
        <img src="https://www.webhostingworld.net/img/pages/whm-logo-new@2x.png" alt="WHM Login" className="h-8 w-auto object-contain" data-testid="whm-login-image" />
      </a>
    </div>
  );
}

function DomainInfoCard({ data }) {
  return (
    <div className="bg-white border border-slate-200 rounded-none card-brutal flex flex-col animate-slide-up" data-testid="domain-info-card">
      <div className="px-6 py-5 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
        <Globe weight="duotone" className="w-5 h-5 text-[#002FA7]" />
        <h2 className="text-lg font-bold tracking-tight text-slate-950 font-heading">Domain Info</h2>
      </div>
      <div className="p-0 flex flex-col divide-y divide-slate-100">
        <DataRow label="Domain" value={data.domain} testId="domain-name" />
        <DataRow label="Registrar" value={data.registrar} testId="domain-registrar" />
        <DataRow label="IP Address" value={data.ip_address} testId="domain-ip" />
        <div className="data-row flex flex-col px-6 py-3.5" data-testid="domain-nameservers">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-[0.15em] font-body mb-2">
            Nameservers
          </span>
          {data.nameservers && data.nameservers.length > 0 ? (
            <div className="flex flex-col gap-1">
              {data.nameservers.map((ns, i) => (
                <span key={i} className="font-mono text-sm text-slate-950 font-medium">{ns}</span>
              ))}
            </div>
          ) : (
            <span className="font-mono text-sm text-slate-400 italic">Not available</span>
          )}
        </div>
        <div className="data-row flex flex-col px-6 py-3.5" data-testid="domain-mx-records">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-[0.15em] font-body mb-2">
            MX Records
          </span>
          {data.mx_records && data.mx_records.length > 0 ? (
            <div className="flex flex-col gap-2">
              {data.mx_records.map((mx, i) => (
                <div key={i} className="flex flex-col gap-0.5" data-testid={`mx-record-${i}`}>
                  <div className="font-mono text-sm text-slate-950 font-medium flex flex-wrap items-center gap-x-2">
                    <span className="text-slate-500" data-testid={`mx-priority-${i}`}>{mx.priority}</span>
                    <span data-testid={`mx-destination-${i}`}>{mx.destination}</span>
                    <span className="text-slate-500" data-testid={`mx-ip-${i}`}>
                      {mx.ip || "—"}
                    </span>
                  </div>
                  {mx.ip && (
                    <div className="font-mono text-xs text-slate-500 pl-1" data-testid={`mx-ptr-${i}`}>
                      {ipToArpa(mx.ip)} → {mx.ptr || <span className="italic">no PTR</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <span className="font-mono text-sm text-slate-400 italic">Not available</span>
          )}
        </div>
      </div>
    </div>
  );
}

function SSLInfoCard({ data }) {
  return (
    <div className="bg-white border border-slate-200 rounded-none card-brutal flex flex-col animate-slide-up-delay" data-testid="ssl-info-card">
      <div className="px-6 py-5 bg-slate-50 border-b border-slate-200 flex items-center gap-3">
        <ShieldCheck weight="duotone" className="w-5 h-5 text-[#002FA7]" />
        <h2 className="text-lg font-bold tracking-tight text-slate-950 font-heading">SSL Certificate</h2>
      </div>
      <div className="p-0 flex flex-col divide-y divide-slate-100">
        <div className="data-row flex items-center justify-between px-6 py-3.5" data-testid="ssl-status-row">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-[0.15em] font-body shrink-0">
            Status
          </span>
          <StatusBadge status={data.status} />
        </div>
        <DataRow label="Issuer" value={data.issuer} testId="ssl-issuer" />
        <DataRow label="Type" value={data.ssl_type} testId="ssl-type" />
        <DataRow label="Issue Date" value={data.issue_date} testId="ssl-issue-date" />
        <DataRow label="Expiry Date" value={data.expiry_date} testId="ssl-expiry-date" />
        <DataRow
          label="Remaining"
          value={data.remaining_days != null ? `${data.remaining_days} days` : null}
          testId="ssl-remaining-days"
        />
      </div>
    </div>
  );
}

function HomePage() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const resultsRef = useRef(null);
  const [capturing, setCapturing] = useState(false);

  const handleCapture = useCallback(async () => {
    if (!resultsRef.current) return;
    setCapturing(true);
    try {
      const el = resultsRef.current;

      // Force full opacity on all children for clean capture
      const animated = el.querySelectorAll(".animate-slide-up, .animate-slide-up-delay");
      animated.forEach((node) => { node.style.opacity = "1"; node.style.transform = "none"; });

      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        width: el.scrollWidth,
        height: el.scrollHeight,
        windowWidth: el.scrollWidth + 100,
        logging: false,
      });

      // Restore styles
      animated.forEach((node) => { node.style.opacity = ""; node.style.transform = ""; });

      // Copy to clipboard only
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (blob && navigator.clipboard?.write) {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        toast.success("Screenshot copied to clipboard!");
      } else {
        toast.error("Clipboard not supported in this browser");
      }
    } catch {
      toast.error("Screenshot failed");
    } finally {
      setCapturing(false);
    }
  }, [result]);

  const handleCheck = useCallback(async (e) => {
    e.preventDefault();
    const trimmed = domain.trim();
    if (!trimmed) {
      toast.error("Please enter a domain name");
      return;
    }

    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const res = await axios.post(`${API}/domain-check`, { domain: trimmed }, { timeout: 30000 });
      setResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to check domain. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [domain]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="max-w-5xl mx-auto px-6 pt-6 md:pt-10 pb-10">
        <div className="mb-8 flex flex-col items-center md:items-start space-y-3">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center">
              <Globe weight="bold" className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-950 font-heading" data-testid="app-title">
              Domain Check
            </h1>
          </div>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleCheck} className="mb-10" data-testid="search-form">
          <div className="search-wrapper w-full max-w-2xl bg-white border-2 border-slate-200 rounded-xl p-2 flex items-center">
            <MagnifyingGlass weight="bold" className="w-5 h-5 text-slate-400 ml-3 shrink-0" />
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              className="w-full outline-none bg-transparent font-mono text-lg md:text-xl placeholder:text-slate-400 text-slate-950 px-4 py-1"
              data-testid="domain-input"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-[#002FA7] text-white font-semibold py-3 px-8 rounded-lg hover:bg-[#001D6B] transition-colors shadow-md flex items-center gap-2 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="check-button"
            >
              {loading ? (
                <CircleNotch weight="bold" className="w-5 h-5 spinner" />
              ) : (
                <MagnifyingGlass weight="bold" className="w-4 h-4" />
              )}
              {loading ? "Checking..." : "Check"}
            </button>
          </div>
        </form>

        {/* Error */}
        {error && !loading && (
          <div className="mb-8 p-5 border border-rose-200 bg-rose-50 text-rose-800 rounded-lg flex items-start gap-3 max-w-2xl animate-slide-up" data-testid="error-message">
            <WarningCircle weight="bold" className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm font-body">Something went wrong</p>
              <p className="text-sm mt-1 font-body">{error}</p>
            </div>
          </div>
        )}

        {/* Loading Skeletons */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start" data-testid="loading-indicator">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <>
            <div className="relative">
              <button
                onClick={handleCapture}
                disabled={capturing}
                className="absolute -top-10 right-0 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-[#002FA7] transition-colors disabled:opacity-50"
                data-testid="capture-button"
                title="Save screenshot"
              >
                <Camera weight="duotone" className="w-4 h-4" />
                {capturing ? "Saving..." : "Screenshot"}
              </button>
              <div ref={resultsRef} className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start bg-white p-4" data-testid="results-container">
                <DomainInfoCard data={result.domain_info} />
                <SSLInfoCard data={result.ssl_info} />
              </div>
            </div>
            <ActionButtons
              registrar={result.domain_info.registrar}
              ipAddress={result.domain_info.ip_address}
            />
          </>
        )}

        {/* Empty state */}
        {!result && !loading && !error && (
          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <HardDrives weight="duotone" className="w-7 h-7 text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm font-body max-w-sm">
              Enter a domain name above to view its registrar, nameservers, IP address, and SSL certificate details.
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-slate-100 mt-8">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between text-xs text-slate-400 font-body">
          <span>Domain Check Tool</span>
          <div className="flex items-center gap-1.5">
            <ShieldCheck weight="duotone" className="w-3.5 h-3.5" />
            <span>Secure & Private</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/" element={<HomePage />} />
      </Routes>
    </BrowserRouter>
  );
}
